import { canAccessBusiness, canUseAccountingOperation } from "../accessControl";
import { accountingEvidenceErrors, ACCOUNTING_EVIDENCE_DOCUMENT_TYPES, EXPECTATION_DOCUMENT_TYPES } from "../documentIntake";
import { MONTHLY_DECLARATION_IDS } from "../monthlyClose";
import { legacyBankStatementConflict } from "../banking/legacyBankStatementIdentity";

export const PROTOTYPE_INTAKE_MUTATION_CAPABILITIES = Object.freeze({
  mode: "prototype",
  productionReady: false,
  organizationGuard: true,
  managedBusinessGuard: true,
  operationGrant: "intake",
  auditPlan: true,
  immutableSourceObjects: false,
  asyncJobs: false,
  serverTransactional: false,
  sourceStorage: "temporary-browser-preview",
});

const denied = (error = "You do not have access to that managed business.") => ({ ok: false, error });
const publicModeDenied = () => denied("Document intake stays read-only in public mode");

const validBusinessScope = ({ business, organizationId, profile }) => Boolean(
  business
  && business.workspaceId === organizationId
  && canAccessBusiness(profile, business.id)
  && canUseAccountingOperation(profile, "intake")
);

const validItemScope = ({ item, business, organizationId, profile }) => Boolean(
  item
  && item.workspaceId === organizationId
  && item.businessId === business?.id
  && validBusinessScope({ business, organizationId, profile })
);

const withStatus = (item, status, now, actorName, changes = {}) => ({
  ...item,
  ...changes,
  status,
  statusHistory: item.status === status
    ? item.statusHistory
    : [...(item.statusHistory || []), { status, at: now, by: actorName }],
});

const updateRecord = (records, id, nextRecord) => records.map((record) => record.id === id ? nextRecord : record);

export const planIntakeBatchAdd = ({
  createdItems = [],
  records = [],
  business,
  organizationId,
  profile,
  publicMode = false,
}) => {
  if (!validBusinessScope({ business, organizationId, profile })) return denied();
  if (publicMode) return publicModeDenied();
  if (!createdItems.length) return denied("Choose at least one document to upload.");
  if (createdItems.some((item) => !validItemScope({ item, business, organizationId, profile }))) return denied();

  const existingById = new Map(records.map((record) => [record.id, record]));
  const collision = createdItems.find((item) => existingById.has(item.id));
  if (collision) return denied("An intake identity already exists. Upload the source again to create a reviewed duplicate record.");

  return {
    ok: true,
    records: [...records, ...createdItems],
    itemIds: createdItems.map((item) => item.id),
    toast: `${createdItems.length} document${createdItems.length === 1 ? "" : "s"} added to the review queue`,
    audit: {
      action: "Documents uploaded",
      detail: `Added ${createdItems.length} file${createdItems.length === 1 ? "" : "s"} to Document intake`,
      entityType: "business",
      entityId: business.id,
    },
  };
};

const REVIEW_EDIT_FIELDS = new Set(["documentType", "period", "reviewFields", "accountingEvidence"]);

export const planIntakeReviewEdit = ({
  item,
  records = [],
  changes = {},
  business,
  organizationId,
  profile,
  publicMode = false,
}) => {
  if (!validItemScope({ item, business, organizationId, profile })) return denied();
  if (publicMode) return publicModeDenied();
  if (item.status === "Accepted") return denied("Accepted documents remain immutable in the audit trail");
  if (Object.keys(changes).some((field) => !REVIEW_EDIT_FIELDS.has(field))) return denied("Only review fields can be changed from Document intake.");

  const nextItem = { ...item, ...changes };
  if (JSON.stringify(nextItem) === JSON.stringify(item)) return { ok: true, noop: true };
  return {
    ok: true,
    records: updateRecord(records, item.id, nextItem),
    item: nextItem,
  };
};

export const planLocalBankStatementReparse = ({
  item,
  expectedItem,
  records = [],
  changes = {},
  extraction,
  business,
  organizationId,
  profile,
  publicMode = false,
  now = new Date().toISOString(),
}) => {
  if (!expectedItem || JSON.stringify(item) !== JSON.stringify(expectedItem)) return denied("The document changed while its statement was being read. Review the current version and try again.");
  if (changes.documentType !== "Bank statement" || !Array.isArray(extraction?.transactions) || !extraction.transactions.length) {
    return denied("The retained source did not produce a reviewable bank statement.");
  }
  if (['Accepted','Duplicate'].includes(item?.status)) return denied('Accepted or duplicate sources cannot be re-read into a new statement review.');
  if (item?.fileHash && extraction.fileHash !== item.fileHash) return denied('The retained source fingerprint changed. No extraction or review was replaced.');
  const review = planIntakeReviewEdit({ item, records, changes, business, organizationId, profile, publicMode });
  if (!review.ok) return review;
  const nextItem = withStatus(review.item || item, "Needs review", now, profile.name, {
    extraction,
    ...(item.extraction && JSON.stringify(item.extraction)!==JSON.stringify(extraction) ? {bankExtractionHistory:[...(item.bankExtractionHistory || []),{extraction:item.extraction,parserVersion:item.parserVersion,sourceHash:item.fileHash,replacedAt:now}]} : {}),
    parserVersion: extraction.parserVersion,
    warning: (extraction.warnings || []).join(" "),
    error: "",
    parserError: "",
    bankParserError: "",
  });
  return {
    ok: true,
    records: updateRecord(records, item.id, nextItem),
    item: nextItem,
    audit: {
      action: "Bank statement classified",
      detail: `Parsed ${item.originalFilename} for accountant review`,
      entityType: "intake",
      entityId: item.id,
      businessId: item.businessId,
    },
  };
};

export const planIntakeReady = ({
  item,
  records = [],
  business,
  organizationId,
  profile,
  publicMode = false,
  bankingEnabled = false,
  now = new Date().toISOString(),
}) => {
  if (!validItemScope({ item, business, organizationId, profile })) return denied();
  if (publicMode) return publicModeDenied();
  if (item.status === "Ready") return { ok: true, noop: true };
  if (item.status === "Accepted") return denied("Accepted documents remain immutable in the audit trail");
  if (["Duplicate", "Unsupported", "Failed"].includes(item.status)) return denied("Resolve the processing exception before completing review");
  if (item.documentType === "Unknown document") return denied("Choose a document type before completing review");
  if (item.documentType === "Bank statement" && !bankingEnabled) return denied("Enable Banking before completing a bank-statement review");
  if (item.error || item.status === "Unsupported") return denied("Resolve the processing error before completing review");
  const evidenceErrors = accountingEvidenceErrors(item);
  if (evidenceErrors.length) return denied(evidenceErrors[0]);

  const nextItem = withStatus(item, "Ready", now, profile.name, {
    warning: "",
    reviewedAt: now,
    reviewedBy: profile.name,
  });
  return {
    ok: true,
    records: updateRecord(records, item.id, nextItem),
    item: nextItem,
    toast: `${item.originalFilename} is ready to accept`,
    audit: {
      action: "Document review completed",
      detail: `Marked ${item.originalFilename} ready for acceptance`,
      entityType: "intake",
      entityId: item.id,
    },
  };
};

export const planDocumentExpectationSave = ({
  expectation,
  records = [],
  business,
  organizationId,
  profile,
  publicMode = false,
  now = new Date().toISOString(),
}) => {
  if (!validBusinessScope({ business, organizationId, profile }) || expectation?.businessId !== business.id) return denied();
  if (publicMode) return publicModeDenied();
  if (!/^\d{4}-\d{2}$/.test(expectation.period || "")) return denied("Choose a valid monthly accounting period.");
  const documentTypes = [...new Set(expectation.documentTypes || [])];
  if (!documentTypes.length || documentTypes.some((type) => !EXPECTATION_DOCUMENT_TYPES.includes(type))) {
    return denied("Choose at least one supported document type.");
  }
  const completedDocumentTypes = [...new Set(expectation.completedDocumentTypes || [])];
  if (completedDocumentTypes.some((type) => !documentTypes.includes(type))) {
    return denied("Completed documents must be part of this monthly checklist.");
  }
  const declarationTypes = [...new Set(expectation.declarationTypes || [])];
  if (declarationTypes.some((type) => !MONTHLY_DECLARATION_IDS.includes(type))) {
    return denied("Choose only supported declaration types.");
  }
  const completedDeclarations = [...new Set(expectation.completedDeclarations || [])];
  if (completedDeclarations.some((type) => !declarationTypes.includes(type))) {
    return denied("Completed declarations must be required for this monthly checklist.");
  }
  const deadline = String(expectation.deadline || "").trim();
  if (deadline && !/^\d{4}-\d{2}-\d{2}$/.test(deadline)) {
    return denied("Choose a valid document request deadline.");
  }

  const id = `${business.id}-${expectation.period}`;
  const existing = records.find((record) => record.id === id);
  if (existing && (existing.workspaceId !== organizationId || existing.businessId !== business.id)) {
    return denied("That checklist identity belongs to another organization or managed business.");
  }
  const record = {
    ...expectation,
    id,
    workspaceId: organizationId,
    businessId: business.id,
    documentTypes,
    completedDocumentTypes,
    declarationTypes,
    completedDeclarations,
    deadline,
    updatedAt: now,
    updatedBy: profile.name,
  };
  return {
    ok: true,
    records: existing
      ? records.map((item) => item.id === id ? record : item)
      : [record, ...records],
    record,
    toast: `Monthly close saved for ${expectation.period}`,
    audit: {
      action: "Monthly close checklist saved",
      detail: `Saved ${completedDocumentTypes.length}/${documentTypes.length} documents and ${completedDeclarations.length}/${declarationTypes.length} declarations for ${expectation.period}`,
      entityType: "business",
      entityId: business.id,
    },
  };
};

export const planIntakeAccept = ({
  item,
  records = [],
  bankTransactions = [],
  business,
  organizationId,
  profile,
  publicMode = false,
  bankingEnabled = false,
  now = new Date().toISOString(),
}) => {
  if (!validItemScope({ item, business, organizationId, profile })) return denied();
  if (publicMode) return publicModeDenied();
  if (item.status !== "Ready") return denied("Complete the document review before accepting it");
  if (item.documentType === "Unknown document") return denied("Choose a document type before accepting this file");

  let nextTransactions = bankTransactions;
  let newTransactionCount = 0;
  if (item.documentType === "Bank statement") {
    if (!bankingEnabled || !item.extraction) return denied("Enable Banking and parse this statement before accepting it");
    if (item.extraction.adapter === "bkt-pdf" && item.extraction.balanced !== true) {
      return denied("This BKT statement does not reconcile and cannot be accepted yet");
    }
    const prepared = (item.extraction.transactions || []).map((transaction) => ({
      ...transaction,
      workspaceId: organizationId,
      businessId: business.id,
      statementId: transaction.statementId || item.id,
      intakeId: item.id,
      reconciliationStatus: "Unmatched",
      evidenceStatus: transaction.signedAmount < 0 ? "Missing evidence" : "Not required",
    }));
    const crossScopeCollision = prepared.find((transaction) => bankTransactions.some((existing) => existing.id === transaction.id
      && (existing.workspaceId !== organizationId || existing.businessId !== business.id)));
    if (crossScopeCollision) return denied("A transaction identity belongs to another organization or managed business.");
    const legacyConflict = legacyBankStatementConflict(item, bankTransactions);
    if (legacyConflict) return denied(legacyConflict);
    if (prepared.some(transaction => typeof transaction.id !== 'string' || !transaction.id.trim()) || new Set(prepared.map(transaction => transaction.id)).size !== prepared.length) {
      return denied('This statement has missing or repeated bank entry identities. Re-read the retained source; no rows were imported.');
    }
    const knownIds = new Set(bankTransactions.map((transaction) => transaction.id));
    const additions = prepared.filter((transaction) => !knownIds.has(transaction.id));
    newTransactionCount = additions.length;
    nextTransactions = [...bankTransactions, ...additions];
  }

  const accountingEvidence = ACCOUNTING_EVIDENCE_DOCUMENT_TYPES.includes(item.documentType)
    ? { ...item.accountingEvidence, confirmedAt: now, confirmedBy: profile.name }
    : item.accountingEvidence;
  const nextItem = withStatus(item, "Accepted", now, profile.name, {
    acceptedAt: now,
    acceptedBy: profile.name,
    accountingEvidence,
  });
  const isStatement = item.documentType === "Bank statement";
  return {
    ok: true,
    records: updateRecord(records, item.id, nextItem),
    bankTransactions: nextTransactions,
    item: nextItem,
    newTransactionCount,
    toast: isStatement
      ? `${newTransactionCount} new transaction${newTransactionCount === 1 ? "" : "s"} accepted; duplicates were ignored`
      : `${item.originalFilename} accepted`,
    audit: {
      action: isStatement ? "Bank statement accepted" : "Document accepted",
      detail: isStatement
        ? `Confirmed ${newTransactionCount} new transactions from ${item.originalFilename}`
        : `Confirmed ${item.documentType} classification for ${item.originalFilename}`,
      entityType: "intake",
      entityId: item.id,
    },
  };
};

export const planIntakeRemove = ({
  item,
  records = [],
  business,
  organizationId,
  profile,
  publicMode = false,
}) => {
  if (!validItemScope({ item, business, organizationId, profile })) return denied();
  if (publicMode) return publicModeDenied();
  if (item.status === "Accepted") return denied("Accepted documents remain in the audit trail");
  return {
    ok: true,
    records: records.filter((candidate) => candidate.id !== item.id),
    previewUrl: item.previewUrl || "",
    toast: `${item.originalFilename} removed from the queue`,
    audit: {
      action: "Document removed",
      detail: `Removed ${item.originalFilename} from the review queue`,
      entityType: "intake",
      entityId: item.id,
    },
  };
};

export const intakeMutationRepository = Object.freeze({
  capabilities: PROTOTYPE_INTAKE_MUTATION_CAPABILITIES,
  addBatch: planIntakeBatchAdd,
  editReview: planIntakeReviewEdit,
  reparseStatement: planLocalBankStatementReparse,
  markReady: planIntakeReady,
  saveExpectation: planDocumentExpectationSave,
  accept: planIntakeAccept,
  remove: planIntakeRemove,
});
